import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MapPin, Star, GraduationCap, Clock, Wallet, Briefcase, 
  ChevronRight, ExternalLink, Target, Heart
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import CareerFormations from './CareerFormations';

// Domain colors for badges
const domainColors = {
  social: { bg: '#FCE4EC', text: '#E91E63', border: '#F48FB1' },
  education: { bg: '#F3E5F5', text: '#9C27B0', border: '#CE93D8' },
  health: { bg: '#FFEBEE', text: '#F44336', border: '#EF9A9A' },
  creativity: { bg: '#FFF3E0', text: '#FF9800', border: '#FFCC80' },
  technology: { bg: '#E3F2FD', text: '#2196F3', border: '#90CAF9' },
  technical: { bg: '#ECEFF1', text: '#607D8B', border: '#B0BEC5' },
  science: { bg: '#E8F5E9', text: '#4CAF50', border: '#A5D6A7' },
  business: { bg: '#EDE7F6', text: '#673AB7', border: '#B39DDB' },
  administration: { bg: '#EFEBE9', text: '#795548', border: '#BCAAA4' },
  sports: { bg: '#E0F7FA', text: '#00BCD4', border: '#80DEEA' }
};

export default function CareerDetailModal({ career, isOpen, onClose }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  if (!isOpen || !career) return null;
  
  const texts = {
    fr: {
      salary: "Salaire indicatif",
      studies: "Formation requise",
      typicalDay: "Journée type",
      workEnv: "Environnement de travail",
      skills: "Compétences clés",
      domains: "Domaines",
      interested: "Ce métier m'intéresse !",
      takeTest: "Faire le test Ikigai",
      learnMore: "En savoir plus",
      close: "Fermer"
    },
    nl: {
      salary: "Indicatief salaris",
      studies: "Vereiste opleiding",
      typicalDay: "Typische dag",
      workEnv: "Werkomgeving",
      skills: "Belangrijke vaardigheden",
      domains: "Domeinen",
      interested: "Dit beroep interesseert me!",
      takeTest: "Doe de Ikigai test",
      learnMore: "Meer informatie",
      close: "Sluiten"
    }
  };
  
  const txt = texts[language] || texts.fr;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
          />
          
          {/* Modal Container - centered using flex */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col w-full max-w-[600px] max-h-[85vh] pointer-events-auto"
            >
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] p-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold pr-8">{career.title}</h2>
              </div>
              
              <p className="text-white/90 text-sm leading-relaxed">
                {career.description}
              </p>
              
              {/* Domain badges */}
              <div className="flex flex-wrap gap-2 mt-4">
                {career.domains?.map((domain, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full"
                  >
                    {t(`domains.${domain}`)}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              
              {/* Salary */}
              {career.salary_range && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="flex items-start gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200"
                >
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800 text-sm mb-1">{txt.salary}</h3>
                    <p className="text-green-700 font-bold text-lg">{career.salary_range}</p>
                  </div>
                </motion.div>
              )}
              
              {/* Studies */}
              {career.studies && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                  className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
                >
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800 text-sm mb-1">{txt.studies}</h3>
                    <p className="text-blue-700">{career.studies}</p>
                  </div>
                </motion.div>
              )}
              
              {/* Typical Day */}
              {career.typical_day && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-start gap-4 p-4 bg-gradient-to-r from-[#FFF3E0] to-[#FFE0B2] rounded-xl border border-[#FFB74D]"
                >
                  <div className="w-10 h-10 bg-[#FB6823] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-orange-800 text-sm mb-1">{txt.typicalDay}</h3>
                    <p className="text-orange-700 text-sm leading-relaxed">{career.typical_day}</p>
                  </div>
                </motion.div>
              )}
              
              {/* Work Environment */}
              {career.work_environment && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200"
                >
                  <div className="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-700 text-sm mb-1">{txt.workEnv}</h3>
                    <p className="text-gray-600">{career.work_environment}</p>
                  </div>
                </motion.div>
              )}
              
              {/* Skills */}
              {career.skills && career.skills.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="p-4 bg-[#F3E5F5] rounded-xl border border-[#CE93D8]"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-5 h-5 text-[#A4187A]" />
                    <h3 className="font-semibold text-[#A4187A] text-sm">{txt.skills}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {career.skills.map((skill, idx) => (
                      <motion.span
                        key={idx}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.35 + idx * 0.05 }}
                        className="px-3 py-1.5 bg-white text-[#A4187A] rounded-lg text-sm font-medium shadow-sm"
                      >
                        {skill}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              )}
              
              {/* Formations Section */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <CareerFormations 
                  careerName={career.title || career.name} 
                  language={language}
                  autoExpand={true}
                />
              </motion.div>
            </div>
            
            {/* Footer with CTA */}
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    onClose();
                    navigate('/questionnaire');
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#A4187A] to-[#FB6823] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity shadow-lg"
                >
                  <Heart className="w-5 h-5" />
                  {txt.interested}
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-3 border-2 border-gray-300 text-gray-600 rounded-xl font-medium hover:bg-gray-100 transition-colors"
                >
                  {txt.close}
                </button>
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
